import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CombosList.css';
import { API_V1, getImageUrl, fetchApi } from '../config/api';
import { fallbackImageForExtra, isRestaurantExtra } from '../utils/extraImages';
import { refAmount } from '../utils/money';
import rollDinamitaImage from '../assets/Tipos de Rolls/ROLL Camaron - Dinamita.png';
import frecuraImage from '../assets/icons/Sushi.png';
import foodImage from '../assets/icons/Vegan Food.png';
import deliveryImage from '../assets/icons/Scooter.png';
import pickupImage from '../assets/icons/Address.png';

const AVAILABILITY_ALL = '';
const AVAILABILITY_AVAILABLE = 'available';
const AVAILABILITY_UNAVAILABLE = 'unavailable';
const SORT_PRICE_ASC = 'price_asc';
const SORT_PRICE_DESC = 'price_desc';
const SORT_NAME_ASC = 'name_asc';
const SORT_NAME_DESC = 'name_desc';

/** Etiquetas para el breadcrumb por slug de categoría */
const CATEGORY_LABELS = {
  todos: 'Todos',
  'rolls-mixtos': 'Sushi',
  'pollo-crispy': 'Pollo crispy',
  extra: 'Extra',
};

function extraPriceLabel(extra) {
  const prices = (extra.flavors || [])
    .map((flavor) => Number(flavor.price))
    .filter((price) => !Number.isNaN(price));
  if (extra.requires_flavor && prices.length) {
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const formatted = min.toFixed(2);
    return min === max ? formatted : `Desde ${formatted}`;
  }
  return refAmount(extra.price_eur);
}

const CombosList = ({ category: categorySlug }) => {
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availabilityFilter, setAvailabilityFilter] = useState(AVAILABILITY_ALL);
  const [priceFilter, setPriceFilter] = useState('');
  const [sortBy, setSortBy] = useState('');

  useEffect(() => {
    const fetchCombos = async () => {
      try {
        setError(null);
        setLoading(true);
        const params = new URLSearchParams();
        params.set('per_page', '100');
        if (categorySlug) params.set('category', categorySlug);
        const extrasParams = new URLSearchParams({
          per_page: '100',
          is_active: '1',
        });
        const [res, extrasRes] = await Promise.all([
          fetchApi(`${API_V1}/combos?${params.toString()}`),
          categorySlug
            ? Promise.resolve(null)
            : fetchApi(`${API_V1}/extras?${extrasParams.toString()}`),
        ]);
        if (!res.ok) throw new Error('Error al cargar combos');
        const json = await res.json();
        const list = json.data ?? json;
        const rawItems = Array.isArray(list) ? list : (list && Array.isArray(list.data) ? list.data : []);
        const comboItems = rawItems
          .map((c) => ({
            key: `combo-${c.id ?? c.combo_id}`,
            href: `/combo/${c.id ?? c.combo_id}`,
            name: c.name || 'Combo',
            price: refAmount(c.price_eur),
            price_eur: Number(c.price_eur) || 0,
            image: getImageUrl(c.image_url ?? c.thumbnail ?? c.thumbnail_url ?? c.image),
            category: CATEGORY_LABELS[c.category] || c.category,
            is_active: c.is_active === true || c.is_active === 1,
          }))
          .filter((c) => c.href !== '/combo/undefined' && c.href !== '/combo/null');

        let extraItems = [];
        if (extrasRes?.ok) {
          const extrasJson = await extrasRes.json();
          const extrasList = extrasJson.data ?? extrasJson;
          const rawExtras = Array.isArray(extrasList) ? extrasList : [];
          extraItems = rawExtras
            .filter(isRestaurantExtra)
            .map((extra) => {
              const id = extra.id ?? extra.extra_id;
              const name = extra.title ?? extra.name ?? 'Extra';
              return {
                key: `extra-${id}`,
                href: `/extra/${id}`,
                name,
                price: extraPriceLabel(extra),
                price_eur: Number(extra.price_eur) || 0,
                image:
                  getImageUrl(extra.image_url ?? extra.image ?? extra.thumbnail) ||
                  fallbackImageForExtra(name),
                category: 'Extra',
                is_active: extra.is_active !== false && extra.is_active !== 0,
              };
            })
            .filter((extra) => extra.href !== '/extra/undefined');
        }

        setCombos([...comboItems, ...extraItems]);
      } catch (err) {
        setError(err.message);
        setCombos([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCombos();
  }, [categorySlug]);

  const filteredAndSortedCombos = React.useMemo(() => {
    let list = [...combos];
    if (availabilityFilter === AVAILABILITY_AVAILABLE) list = list.filter((c) => c.is_active);
    if (availabilityFilter === AVAILABILITY_UNAVAILABLE) list = list.filter((c) => !c.is_active);
    const order =
      sortBy ||
      (priceFilter === 'asc'
        ? SORT_PRICE_ASC
        : priceFilter === 'desc'
        ? SORT_PRICE_DESC
        : '');
    if (order === SORT_PRICE_ASC) list.sort((a, b) => (a.price_eur ?? 0) - (b.price_eur ?? 0));
    if (order === SORT_PRICE_DESC) list.sort((a, b) => (b.price_eur ?? 0) - (a.price_eur ?? 0));
    if (order === SORT_NAME_ASC) list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
    if (order === SORT_NAME_DESC) list.sort((a, b) => (b.name || '').localeCompare(a.name || '', 'es'));
    return list;
  }, [combos, availabilityFilter, priceFilter, sortBy]);

  const features = [
    {
      id: 1,
      title: 'FRESCURA',
      description: 'INGREDIENTES FRESCOS SELECCIONADOS PARA TU SUSHI',
      image: frecuraImage
    },
    {
      id: 2,
      title: 'COMIDA DELICIOSA',
      description: 'SABOR AUTENTICO QUE SE DISFRUTA EN CADA BOCADO',
      image: foodImage
    },
    {
      id: 3,
      title: 'DELIVERY',
      description: 'TU SUSHI FAVORITO RÁPIDO Y SEGURO HASTA TU PUERTA',
      image: deliveryImage
    },
    {
      id: 4,
      title: 'PICK UP',
      description: 'ORDENA ONLINE Y RECOGE SIN FILAS NI ESPERAS',
      image: pickupImage
    }
  ];

  return (
    <section className="combos-list">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Inicio</Link>
          <span> / </span>
          <Link to="/combos">COMBOS</Link>
          {categorySlug && (
            <>
              <span> / </span>
              <span>{CATEGORY_LABELS[categorySlug] || categorySlug}</span>
            </>
          )}
        </div>

        <h1 className="combos-title">
          {categorySlug ? (CATEGORY_LABELS[categorySlug] || categorySlug) : 'COMBOS'}
        </h1>

        {error && (
          <p className="combos-error">No se pudieron cargar los combos. Verifica que la API esté disponible.</p>
        )}
        {loading && <p className="combos-loading">Cargando combos...</p>}

        <div className="combos-header">
          <div className="combos-filters">
            <div className="filter-group">
              <label>Filtrar:</label>
              <select
                className="filter-select"
                value={availabilityFilter}
                onChange={(e) => setAvailabilityFilter(e.target.value)}
              >
                <option value={AVAILABILITY_ALL}>Disponibilidad</option>
                <option value={AVAILABILITY_AVAILABLE}>Disponible</option>
                <option value={AVAILABILITY_UNAVAILABLE}>No disponible</option>
              </select>
              <select
                className="filter-select"
                value={priceFilter}
                onChange={(e) => setPriceFilter(e.target.value)}
              >
                <option value="">Precio</option>
                <option value="asc">Menor a mayor</option>
                <option value="desc">Mayor a menor</option>
              </select>
            </div>
            <div className="combos-count">
              <span>
                {filteredAndSortedCombos.length}{' '}
                {categorySlug ? 'combos' : 'productos'}
              </span>
            </div>
          </div>
          <div className="combos-sort">
            <label>Ordenar por:</label>
            <select
              className="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="">Seleccionar</option>
              <option value={SORT_PRICE_ASC}>Precio: menor a mayor</option>
              <option value={SORT_PRICE_DESC}>Precio: mayor a menor</option>
              <option value={SORT_NAME_ASC}>Nombre A-Z</option>
              <option value={SORT_NAME_DESC}>Nombre Z-A</option>
            </select>
          </div>
        </div>

        <div className="combos-grid">
          {!loading && !error && filteredAndSortedCombos.map((combo) => (
            <Link key={combo.key} to={combo.href} className="combo-card">
              <div className="combo-image-container">
                <img
                  src={combo.image || rollDinamitaImage}
                  alt={combo.name}
                  className="combo-image"
                  onError={(e) => {
                    const fallback = fallbackImageForExtra(combo.name);
                    if (fallback && e.currentTarget.src !== fallback) {
                      e.currentTarget.src = fallback;
                      return;
                    }
                    e.currentTarget.src = rollDinamitaImage;
                  }}
                />
              </div>
              <div className="combo-content">
                <span className="combo-ref">Ref {combo.price}</span>
                <h3 className="combo-name">{combo.name}</h3>
                {combo.category && (
                  <p className="combo-category">{combo.category}</p>
                )}
                <span className="add-to-cart-btn">Ver detalle</span>
              </div>
            </Link>
          ))}
        </div>

        <div className="combos-features">
          {features.map((feature) => (
            <div key={feature.id} className="combo-feature">
              <div className="combo-feature-icon">
                <img 
                  src={feature.image} 
                  alt={feature.title}
                  className="combo-feature-img"
                />
              </div>
              <div className="combo-feature-content">
                <h3 className="combo-feature-title">{feature.title}</h3>
                <p className="combo-feature-description">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CombosList;

